/**
 * Figma `57:2`'s Convites rail card, below the organisation list: every
 * invitation `useMyInvitations` returns for the caller's own verified
 * address, each with its own Aceitar and Recusar
 * (`owner-decisions-align-57-2-01`, `...-57-2-02`).
 *
 * `campaign_name` is not on `GET /invitations` yet -- `...-57-2-02` ships it
 * as a full-stack change -- so the second line names the role alone until
 * it lands.
 *
 * One `useAcceptInvitation()`/`useDeclineInvitation()` instance for the
 * whole list, not one per row, the same reasoning `MyInvitations.tsx` uses:
 * only one row can be mid-mutation at a time, and `mutation.variables` is
 * how a row tells whether the last call was its own.
 */
import type { ReactNode } from 'react';
import type { UseMutationResult } from '@tanstack/react-query';

import type { ApiFailureError } from '../../../lib/http/errors';
import { Badge } from '../../../ui/badge';
import { Button } from '../../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../ui/card';
import { Skeleton } from '../../../ui/skeleton';
import { useAuthState } from '../authState';
import { ta } from '../../tenant/accept/messages';
import type { AcceptResponse, DeclineResponse, MyInvitation } from '../../tenant/accept/schemas';
import {
  useAcceptInvitation,
  type AcceptInvitationInput,
} from '../../tenant/accept/useAcceptInvitation';
import {
  useDeclineInvitation,
  type DeclineInvitationInput,
} from '../../tenant/accept/useDeclineInvitation';
import { useMyInvitations } from '../../tenant/accept/useMyInvitations';
import { tt } from '../../tenant/i18n';
import { sw } from './messages';

type Acceptance = UseMutationResult<AcceptResponse, ApiFailureError, AcceptInvitationInput>;
type Decline = UseMutationResult<DeclineResponse, ApiFailureError, DeclineInvitationInput>;

function isExpired(invitation: MyInvitation): boolean {
  return invitation.state === 'PENDING' && new Date(invitation.expires_at).getTime() < Date.now();
}

function InvitationRailRow(props: {
  invitation: MyInvitation;
  acceptance: Acceptance;
  decline: Decline;
}): ReactNode {
  const { invitation, acceptance, decline } = props;
  const expired = isExpired(invitation);

  const acceptingThisRow =
    acceptance.variables !== undefined &&
    'invitationId' in acceptance.variables &&
    acceptance.variables.invitationId === invitation.id;
  const decliningThisRow =
    decline.variables !== undefined &&
    'invitationId' in decline.variables &&
    decline.variables.invitationId === invitation.id;
  const busy =
    (acceptingThisRow && acceptance.isPending) || (decliningThisRow && decline.isPending);

  return (
    <div className="flex flex-col gap-2 border-b pb-4 last:border-b-0 last:pb-0">
      <div>
        <p className="text-body-sm font-medium">
          {sw('invitations.invitedBy', { tenant: invitation.tenant_name })}
        </p>
        <p className="text-caption text-muted-foreground">{ta(`role.${invitation.role}`)}</p>
      </div>
      {invitation.state === 'WITHDRAWN' ? (
        <p className="text-body-sm text-muted-foreground">{tt('accept.invite.withdrawn_inline')}</p>
      ) : expired ? (
        <p className="text-body-sm text-muted-foreground">{ta('blocked.expired')}</p>
      ) : (
        <>
          {acceptingThisRow && acceptance.isError && (
            <p role="alert" className="text-body-sm text-destructive">
              {sw('invitations.acceptFailed')}
            </p>
          )}
          {decliningThisRow && decline.isError && (
            <p role="alert" className="text-body-sm text-destructive">
              {ta('declineFailed')}
            </p>
          )}
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={() => decline.mutate({ invitationId: invitation.id })}
            >
              {ta('action.decline')}
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={busy}
              onClick={() => acceptance.mutate({ invitationId: invitation.id })}
            >
              {tt('accept.invite.accept_action')}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

export function InvitationsRail(): ReactNode {
  const session = useAuthState();
  const query = useMyInvitations();
  const acceptance = useAcceptInvitation();
  const decline = useDeclineInvitation();

  // Mirrors `useMyInvitations`'s own `enabled` gate: a signed-out or
  // unverified caller never asks, and `query.status` would otherwise sit at
  // 'pending' forever -- rendering nothing here is what keeps that from
  // reading as a stuck spinner.
  if (session.status !== 'signed-in' || session.user?.emailVerified !== true) {
    return null;
  }

  if (query.status === 'pending') {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{tt('accept.page_title')}</CardTitle>
        </CardHeader>
        <CardContent
          className="flex flex-col gap-2"
          role="status"
          aria-label={tt('accept.page_title')}
        >
          <Skeleton aria-hidden className="h-4 w-full" />
          <Skeleton aria-hidden className="h-4 w-2/3" />
        </CardContent>
      </Card>
    );
  }

  if (query.status === 'error') {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{tt('accept.page_title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <p role="alert" className="text-body-sm text-destructive">
            {sw('invitations.error')}
          </p>
        </CardContent>
      </Card>
    );
  }

  const items = query.data;
  if (items.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {tt('accept.page_title')}
          <Badge variant="secondary">{items.length}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {items.map((invitation) => (
          <InvitationRailRow
            key={invitation.id}
            invitation={invitation}
            acceptance={acceptance}
            decline={decline}
          />
        ))}
      </CardContent>
    </Card>
  );
}
